// Maps every Mexican state (ISO 3166-2:MX code) to one of the 4
// shipping zones defined as theme settings, and to its Spanish display
// name for the "change my state" picker.
const STATE_TO_ZONE = {
  'MX-CMX': 'cdmx',
  'MX-MEX': 'cdmx',
  'MX-JAL': 'bajio',
  'MX-GUA': 'bajio',
  'MX-QUE': 'bajio',
  'MX-AGU': 'bajio',
  'MX-SLP': 'bajio',
  'MX-HID': 'bajio',
  'MX-MOR': 'bajio',
  'MX-PUE': 'bajio',
  'MX-TLA': 'bajio',
  'MX-MIC': 'bajio',
  'MX-COL': 'bajio',
  'MX-NLE': 'norte',
  'MX-TAM': 'norte',
  'MX-COA': 'norte',
  'MX-CHH': 'norte',
  'MX-DUR': 'norte',
  'MX-ZAC': 'norte',
  'MX-SON': 'norte',
  'MX-SIN': 'norte',
  'MX-BCN': 'norte',
  'MX-BCS': 'norte',
  'MX-OAX': 'sureste',
  'MX-CHP': 'sureste',
  'MX-GRO': 'sureste',
  'MX-VER': 'sureste',
  'MX-TAB': 'sureste',
  'MX-CAM': 'sureste',
  'MX-YUC': 'sureste',
  'MX-ROO': 'sureste',
  'MX-NAY': 'sureste',
};

const STATE_NAMES = {
  'MX-AGU': 'Aguascalientes',
  'MX-BCN': 'Baja California',
  'MX-BCS': 'Baja California Sur',
  'MX-CAM': 'Campeche',
  'MX-CHP': 'Chiapas',
  'MX-CHH': 'Chihuahua',
  'MX-CMX': 'Ciudad de México',
  'MX-COA': 'Coahuila',
  'MX-COL': 'Colima',
  'MX-DUR': 'Durango',
  'MX-GUA': 'Guanajuato',
  'MX-GRO': 'Guerrero',
  'MX-HID': 'Hidalgo',
  'MX-JAL': 'Jalisco',
  'MX-MIC': 'Michoacán',
  'MX-MOR': 'Morelos',
  'MX-MEX': 'Estado de México',
  'MX-NAY': 'Nayarit',
  'MX-NLE': 'Nuevo León',
  'MX-OAX': 'Oaxaca',
  'MX-PUE': 'Puebla',
  'MX-QUE': 'Querétaro',
  'MX-ROO': 'Quintana Roo',
  'MX-SLP': 'San Luis Potosí',
  'MX-SIN': 'Sinaloa',
  'MX-SON': 'Sonora',
  'MX-TAB': 'Tabasco',
  'MX-TAM': 'Tamaulipas',
  'MX-TLA': 'Tlaxcala',
  'MX-VER': 'Veracruz',
  'MX-YUC': 'Yucatán',
  'MX-ZAC': 'Zacatecas',
};

const FALLBACK_STATE = 'MX-CMX';
const STORAGE_KEY = 'generra_shipping_state';
const GEOLOCATION_ENDPOINT = 'https://api.bigdatacloud.net/data/reverse-geocode-client?localityLanguage=es';
const GEOLOCATION_TIMEOUT_MS = 2500;

async function detectStateCode() {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && STATE_TO_ZONE[stored]) return stored;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), GEOLOCATION_TIMEOUT_MS);

  try {
    const response = await fetch(GEOLOCATION_ENDPOINT, { signal: controller.signal });
    const data = await response.json();
    clearTimeout(timeout);

    if (data.countryCode !== 'MX') return FALLBACK_STATE;

    const code = data.principalSubdivisionCode;
    if (code && STATE_TO_ZONE[code]) return code;

    return FALLBACK_STATE;
  } catch (error) {
    clearTimeout(timeout);
    return FALLBACK_STATE;
  }
}

function initDeliveryEstimate(root) {
  const dataScript = root.querySelector('.js-delivery-estimate-data');
  if (!dataScript) return;

  let payload;
  try {
    payload = JSON.parse(dataScript.textContent);
  } catch (e) {
    return;
  }

  const zonesById = payload.zones.reduce((acc, zone) => {
    acc[zone.id] = zone;
    return acc;
  }, {});

  const headingEl = root.querySelector('.js-delivery-estimate-heading');
  const sublineEl = root.querySelector('.js-delivery-estimate-subline');
  const locationRow = root.querySelector('.js-delivery-estimate-location');
  const locationTextEl = root.querySelector('.js-delivery-estimate-location-text');
  const changeBtn = root.querySelector('.js-delivery-estimate-change');
  const picker = root.querySelector('.js-delivery-estimate-picker');
  const select = root.querySelector('.js-delivery-estimate-select');

  // The server-rendered countdown element. It is moved around rather than
  // recreated, so its timer is never interrupted and no markup is ever
  // written from JS.
  const countdownEl = sublineEl
    ? sublineEl.querySelector('.js-delivery-countdown')
    : null;

  const COUNTDOWN_TOKEN = '[contador]';

  function renderSubline(text) {
    if (!sublineEl) return;

    const tokenIndex = text.indexOf(COUNTDOWN_TOKEN);

    // After the cutoff there is no countdown to place - plain text only.
    if (tokenIndex === -1 || !countdownEl) {
      sublineEl.textContent = text;
      return;
    }

    sublineEl.replaceChildren(
      document.createTextNode(text.slice(0, tokenIndex)),
      countdownEl,
      document.createTextNode(text.slice(tokenIndex + COUNTDOWN_TOKEN.length)),
    );
  }

  function applyState(stateCode) {
    const zoneId = STATE_TO_ZONE[stateCode];
    const zone = zonesById[zoneId];
    if (!zone) return;

    if (headingEl) headingEl.textContent = zone.heading;
    renderSubline(zone.subline);
    if (locationTextEl) {
      locationTextEl.textContent = payload.locationText.replace('[estado]', STATE_NAMES[stateCode] || '');
    }
    if (locationRow) locationRow.hidden = false;
    if (select) select.value = stateCode;
  }

  if (select) {
    const sortedCodes = Object.keys(STATE_NAMES).sort((a, b) =>
      STATE_NAMES[a].localeCompare(STATE_NAMES[b], 'es'),
    );
    sortedCodes.forEach((code) => {
      const option = document.createElement('option');
      option.value = code;
      option.textContent = STATE_NAMES[code];
      select.appendChild(option);
    });

    select.addEventListener('change', () => {
      if (!select.value) return;
      window.localStorage.setItem(STORAGE_KEY, select.value);
      applyState(select.value);
      if (picker) picker.hidden = true;
    });
  }

  if (changeBtn && picker) {
    changeBtn.addEventListener('click', () => {
      picker.hidden = !picker.hidden;
    });
  }

  // A stored choice needs no network call, so apply it immediately - waiting
  // for idle would make the location line flicker in for returning visitors.
  // Only the geolocation lookup (a third-party request that just personalizes
  // an already-correct default) is deferred, so it never competes with the
  // critical path.
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && STATE_TO_ZONE[stored]) {
    applyState(stored);
    return;
  }

  const detect = () => detectStateCode().then(applyState);

  if ('requestIdleCallback' in window) {
    requestIdleCallback(detect, { timeout: 3000 });
  } else {
    setTimeout(detect, 1200);
  }
}

document.querySelectorAll('.js-delivery-estimate').forEach(initDeliveryEstimate);
